'use client';

// Client-side methods catalog — same data the server fetched at
// request time, but with a filter input + system-toggle pills so
// prospects can narrow to "does X support method Y?" without
// scrolling 100+ rows. Filtering is client-only over the server
// payload; the catalog is small enough (sub-1KB per row) that a
// server-side filter would be added latency for no win.

import { useMemo, useState } from 'react';

interface CatalogRow {
  system:        'bchn' | 'fulcrum' | 'chaingraph';
  method:        string;
  cacheable:     boolean;
  ttl_ms:        number | null;
  has_push:      boolean;
  default_cost:  number | null;
  notes:         string | null;
}

const SYSTEMS = ['bchn', 'fulcrum', 'chaingraph'] as const;
type SystemId = (typeof SYSTEMS)[number];

export function MethodsCatalogClient({ rows, generated_at }: {
  rows: CatalogRow[];
  generated_at: string;
}) {
  const [query, setQuery]       = useState('');
  const [enabled, setEnabled]   = useState<Set<SystemId>>(new Set(SYSTEMS));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (!enabled.has(r.system)) return false;
      if (!q) return true;
      return r.method.toLowerCase().includes(q)
          || (r.notes?.toLowerCase().includes(q) ?? false)
          || r.system.includes(q);
    });
  }, [rows, query, enabled]);

  const grouped: Record<string, CatalogRow[]> = {};
  for (const r of filtered) (grouped[r.system] ??= []).push(r);

  function toggle(s: SystemId) {
    const next = new Set(enabled);
    if (next.has(s)) next.delete(s); else next.add(s);
    if (next.size === 0) next.add(s);    // keep at least one on
    setEnabled(next);
  }

  return (
    <div className="space-y-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="text-xs text-ink-faint">
          {filtered.length} of {rows.length} methods · generated {new Date(generated_at).toLocaleString()}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {SYSTEMS.map((s) => (
            <button key={s} onClick={() => toggle(s)}
                    className={`px-3 py-1 text-xs rounded-md ring-1 ring-inset transition capitalize ${
                      enabled.has(s)
                        ? 'bg-accent/15 text-accent ring-accent/30'
                        : 'text-ink-dim ring-white/10 hover:bg-surface-2'
                    }`}>
              {s}
            </button>
          ))}
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="filter methods…"
            className="rounded-md bg-surface-2 ring-1 ring-inset ring-white/5 px-3 py-1 text-xs text-ink placeholder:text-ink-faint focus:outline-none focus:ring-accent/50 font-mono w-48"
          />
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="card text-sm text-ink-dim">
          No methods match <span className="font-mono">{query}</span>{' '}
          {enabled.size < SYSTEMS.length && '(within enabled systems)'}.
        </div>
      )}

      {Object.entries(grouped).map(([system, list]) => (
        <section key={system} id={system}>
          <h2 className="heading-2 mb-4 capitalize">
            {system} <span className="text-ink-faint text-base">({list.length})</span>
          </h2>
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="text-left text-ink-faint">
                <tr className="border-b border-white/5">
                  <th className="font-normal px-4 py-3">Method</th>
                  <th className="font-normal px-4 py-3 text-right">Default CC</th>
                  <th className="font-normal px-4 py-3">Cache</th>
                  <th className="font-normal px-4 py-3">Push</th>
                  <th className="font-normal px-4 py-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={`${r.system}/${r.method}`} className="border-t border-white/5 align-top">
                    <td className="px-4 py-2 font-mono text-xs text-ink break-all">{r.method}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs text-ink">
                      {r.default_cost === null
                        ? <span className="text-ink-faint italic">varies</span>
                        : r.default_cost.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.cacheable
                        ? <span className="text-emerald-300 font-mono">{r.ttl_ms ?? '—'}ms</span>
                        : <span className="text-ink-faint">no</span>}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.has_push
                        ? <span className="text-amber-300 font-mono">subscribe</span>
                        : <span className="text-ink-faint">—</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-ink-dim">{r.notes ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
